import { Alert, AlertProps } from '@platform/components';
import { useEffect, useState } from 'react';

const DISMISSED_STORAGE_KEY = 'connections_dismissed';

type RepUserPendingConnectionsAlertProps = {
  projectIds: number[];
  additionalClassnames?: string;
};

export const RepCancelledPendingConnectionsAlert = ({
  projectIds,
  additionalClassnames = '',
}: RepUserPendingConnectionsAlertProps) => {
  return (
    <PendingConnectionAlert additonalClassnames={additionalClassnames} projectIds={projectIds}>
      One of your projects was matched! The pending introductions and connections associated with it
      have been moved to declined.{' '}
      <a
        target='_blank'
        href='https://zsupport.movingworlds.org/portal/en/kb/articles/why-were-my-pending-introductions-declined'
        className='text-brand-500 hover:underline'
      >
        Learn more
      </a>
    </PendingConnectionAlert>
  );
};

type PendingConnectionAlertProps = Omit<AlertProps, 'onDismiss' | 'variant'> & {
  projectIds: number[];
};

const PendingConnectionAlert = ({
  projectIds,
  children,
  primaryCTA,
  secondaryCTA,
  additonalClassnames = '',
}: PendingConnectionAlertProps) => {
  const [isDismissed, setIsDismissed] = useState(false);

  // listen for dismiss on other tabs
  useEffect(() => {
    const handleLocalStorageChange = (event: StorageEvent) => {
      if (event.key === DISMISSED_STORAGE_KEY) {
        const newActiveIdsOnEvent = getNonDimissedIds(projectIds);
        if (newActiveIdsOnEvent.length === 0) {
          setIsDismissed(true);
        }
      }
    };

    window.addEventListener('storage', handleLocalStorageChange);
    return () => {
      window.removeEventListener('storage', handleLocalStorageChange);
    };
  });

  const onDimissClick = () => {
    handleOnDismiss(projectIds);
    setIsDismissed(true);
  };

  // hacky way to deal with this CTA not actually being a CTA
  if (primaryCTA) {
    const originalSecondaryCallback = primaryCTA.callback;
    primaryCTA.callback = () => {
      originalSecondaryCallback();
      setIsDismissed(true);
    };
  }

  return (
    !isDismissed && (
      <Alert
        variant='warning'
        additonalClassnames={`${additonalClassnames} my-4`}
        primaryCTA={primaryCTA}
        secondaryCTA={secondaryCTA}
        onDismiss={onDimissClick}
      >
        {children}
      </Alert>
    )
  );
};

export const handleOnDismiss = (projectIds: number[]) => {
  const dismissedConnections = JSON.parse(localStorage.getItem(DISMISSED_STORAGE_KEY) || '[]');
  dismissedConnections.push(...projectIds);
  localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(dismissedConnections));
};
/** Clean up local storage of any old/irrelvant project ids, return non dimissed relevant ids*/
export const getNonDimissedIds = (projectIds: number[]) => {
  const currentDismissedConnections = JSON.parse(
    localStorage.getItem(DISMISSED_STORAGE_KEY) || '[]',
  );
  const relevantDismissedIds = currentDismissedConnections.filter((id: number) =>
    projectIds.includes(id),
  );
  if (relevantDismissedIds.length > 0) {
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(relevantDismissedIds));
  } else {
    localStorage.removeItem(DISMISSED_STORAGE_KEY);
  }
  return projectIds.filter((id) => !relevantDismissedIds.includes(id));
};
